/**
 * Logistics List-Load Policy
 *
 * Mirrors `invoicingListLoadPolicy`. Subscribes to `ExternalSystemsIntegrations`
 * and keeps only the provisioned integrations whose connector kind is
 * `logistics`. The integrations readmodel stamps each row's `connectorKind` (see
 * `integrations.readmodel.ts`), so we filter on the row directly rather than
 * making a second hop into the connectors catalog.
 *
 * Unlike invoicing (which keys off a single `issues-invoices` capability),
 * logistics connectors declare heterogeneous capabilities
 * (`pulls-shipment-updates`, `receives-webhooks`, ...), so the `connectorKind`
 * grouping field is the correct discriminator.
 *
 * The filtered, keyed record is dispatched into `data.integrations` via
 * `list/set` (which also clears `loading.listLoading`).
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType, ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import { SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
import { LogisticsState, LogisticsIntegration } from '../state';
import { LogisticsCommand } from '../commands';

/**
 * An integration is a logistics integration iff its readmodel row's
 * `connectorKind` is `logistics`.
 */
const isLogisticsIntegration = (
  integration: SystemIntegrationsReadmodel | undefined
): boolean =>
  integration?.connectorKind === 'logistics';

/**
 * Creates a policy that loads the provisioned logistics integrations from the
 * backend subscription into `data.integrations`.
 */
export const createLogisticsListLoadPolicy = (
  backend: ClientLibraryType
): Policy<LogisticsState, LogisticsCommand> => ({
  name: 'logistics-list-load',

  init: ({ dispatch, getState }) => {
    const subscription = backend.subscriptions.ExternalSystemsIntegrations(
      {},
      ( change: ReadmodelChange<SystemIntegrationsReadmodel> ) => {
        if ( change.type === 'snapshot' ) {
          const logisticsRecord = change.data.items
            .filter( isLogisticsIntegration )
            .reduce<Record<string, LogisticsIntegration>>(
              ( acc, integration ) => {
                acc[integration.id] = integration;
                return acc;
              },
              {}
            );
          dispatch(
            { type: 'integrations/logistics/list/set', payload: { integrations: logisticsRecord }},
            { source: 'api' }
          );
          return;
        }

        if ( change.type === 'signal' ) return;

        const record = change.type === 'delete' ? change.previous : change.data;
        if ( !isLogisticsIntegration( record )) return;

        const current = getState().data.integrations;

        if ( change.type === 'delete' ) {
          const { [change.previous.id]: _removed, ...rest } = current;
          dispatch(
            { type: 'integrations/logistics/list/set', payload: { integrations: rest }},
            { source: 'api' }
          );
        } else {
          dispatch(
            {
              type: 'integrations/logistics/list/set',
              payload: { integrations: { ...current, [change.data.id]: change.data }}
            },
            { source: 'api' }
          );
        }
      }
    );

    return () => {
      subscription?.close();
    };
  }
});
